import { ReactNode, useCallback, useState, useEffect, memo } from 'react'
import { Box, Flex, useDisclosure, useMediaQuery } from '@chakra-ui/react'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { useThemeColors } from '@/hooks/useThemeColors'

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed'

interface AdminLayoutProps {
  children: ReactNode
}

function AdminLayoutComponent({ children }: AdminLayoutProps) {
  const bgColor = useThemeColors().bg
  const mobileDrawer = useDisclosure()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed))
    } catch {
      // ignore
    }
  }, [isCollapsed])

  const handleLogout = useCallback(async () => {
    await logout()
    navigate('/login')
  }, [logout, navigate])

  const handleSidebarClose = useCallback(() => {
    mobileDrawer.onClose()
  }, [mobileDrawer])

  const handleSidebarOpen = useCallback(() => {
    if (isMobile) {
      mobileDrawer.onOpen()
    } else {
      setIsCollapsed((prev) => !prev)
    }
  }, [isMobile, mobileDrawer])

  return (
    <Box h="100vh" overflow="hidden" bg={bgColor}>
      <Flex h="100vh" overflow="hidden">
        <Sidebar
          isOpen={mobileDrawer.isOpen}
          onClose={handleSidebarClose}
          isCollapsed={isCollapsed}
          collapsed={isCollapsed}
        />

        <Box flex="1" bg={bgColor} display="flex" flexDirection="column" minW={0} h="100vh" overflow="hidden">
          <Header onOpen={handleSidebarOpen} user={user} onLogout={handleLogout} />

          <Box as="main" flex="1" overflowY="auto" overflowX="hidden" position="relative">
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

// Memoize AdminLayout to prevent unnecessary re-renders
export const AdminLayout = memo(AdminLayoutComponent)

